import React, { useMemo, useState } from 'react';
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { Employee, SchedulingApprovedHoliday, Shift, ShiftMarketplaceRequest } from '../../types';
import { marketplaceSchedulerBadge } from '../../utils/shiftMarketplacePending';
import { MarketplaceCoverBadge } from './MarketplaceCoverDetailsPopup';
import { SchedulingDayShiftsModal, MonthShiftRow } from './SchedulingDayShiftsModal';

const MAX_VISIBLE_ROWS = 3;

function parseLocalDateTime(dateTimeStr: string): Date {
  if (!dateTimeStr.includes('Z') && !dateTimeStr.includes('+') && !dateTimeStr.includes('-', 10)) {
    const [datePart, timePart] = dateTimeStr.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute, second || 0);
  }
  return new Date(dateTimeStr);
}

function employeeDisplayName(employee: Employee | undefined, shift: Shift): string {
  if (employee) {
    return `${employee.firstName} ${employee.lastName}`.trim();
  }
  if (shift.employee) {
    return `${shift.employee.firstName} ${shift.employee.lastName}`.trim();
  }
  return `Employee ${shift.employeeId}`;
}

function resolveShiftColor(
  shift: Shift,
  colorMode: 'shift' | 'position',
  positions: Array<{ id: number; color?: string }>
): string {
  if (colorMode === 'position' && shift.positionId != null) {
    const position = positions.find((p) => p.id === shift.positionId);
    if (position?.color) return position.color;
  }
  return shift.color || '#3b82f6';
}

export function SchedulingMonthView({
  month,
  weekStartsOn,
  shifts,
  employees,
  searchQuery,
  onSearchChange,
  colorMode,
  onColorModeChange,
  positions,
  approvedHolidays,
  loading,
  marketplaceByShiftId,
}: {
  month: Date;
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  shifts: Shift[];
  employees: Employee[];
  searchQuery: string;
  onSearchChange: (value: string) => void;
  colorMode: 'shift' | 'position';
  onColorModeChange: (mode: 'shift' | 'position') => void;
  positions: Array<{ id: number; color?: string }>;
  approvedHolidays?: SchedulingApprovedHoliday[];
  loading?: boolean;
  marketplaceByShiftId?: Map<number, ShiftMarketplaceRequest>;
}) {
  const [modalDate, setModalDate] = useState<Date | null>(null);

  const employeeById = useMemo(() => {
    const map = new Map<number, Employee>();
    for (const emp of employees) {
      map.set(Number(emp.id), emp);
    }
    return map;
  }, [employees]);

  const searchLower = searchQuery.trim().toLowerCase();

  const rowsByDate = useMemo(() => {
    const map = new Map<string, MonthShiftRow[]>();

    for (const shift of shifts) {
      const employee = employeeById.get(Number(shift.employeeId));
      const employeeName = employeeDisplayName(employee, shift);
      if (searchLower) {
        const email = (employee?.email || shift.employee?.email || '').toLowerCase();
        if (!employeeName.toLowerCase().includes(searchLower) && !email.includes(searchLower)) {
          continue;
        }
      }

      const start = parseLocalDateTime(shift.startTime);
      const end = parseLocalDateTime(shift.endTime);
      const dateStr = format(start, 'yyyy-MM-dd');
      const pending = marketplaceByShiftId?.get(shift.id);
      const row: MonthShiftRow = {
        shift,
        employeeName,
        startLabel: format(start, 'HH:mm'),
        endLabel: format(end, 'HH:mm'),
        color: resolveShiftColor(shift, colorMode, positions),
        marketplaceRequest: pending,
        marketplaceBadge: pending ? marketplaceSchedulerBadge(pending) : undefined,
      };
      const list = map.get(dateStr);
      if (list) list.push(row);
      else map.set(dateStr, [row]);
    }

    map.forEach((list) => {
      list.sort((a, b) => {
        const timeDiff =
          parseLocalDateTime(a.shift.startTime).getTime() -
          parseLocalDateTime(b.shift.startTime).getTime();
        if (timeDiff !== 0) return timeDiff;
        return a.employeeName.localeCompare(b.employeeName);
      });
    });

    return map;
  }, [shifts, employeeById, searchLower, colorMode, positions, marketplaceByShiftId]);

  const holidayDates = useMemo(() => {
    const set = new Set<string>();
    for (const holiday of approvedHolidays ?? []) {
      set.add(holiday.date.split('T')[0]);
    }
    return set;
  }, [approvedHolidays]);

  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const gridStart = startOfWeek(monthStart, { weekStartsOn });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn });
  const gridDays = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const weekDayHeaders = Array.from({ length: 7 }, (_, i) => addDays(gridStart, i));

  const modalRows = modalDate ? rowsByDate.get(format(modalDate, 'yyyy-MM-dd')) ?? [] : [];

  return (
    <div className="flex-1 border rounded-lg flex flex-col relative min-h-0 bg-white">
      {loading && (
        <div className="absolute inset-0 bg-white/75 flex items-center justify-center z-20 rounded-lg">
          <div className="text-sm text-gray-600 font-medium">Loading shifts...</div>
        </div>
      )}

      <div className="border-b bg-gray-50 p-2 flex-shrink-0 flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <input
            type="text"
            placeholder="Search employees..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full px-3 py-2 pl-8 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-700">
          <span className="text-xs font-medium">Color By</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              name="monthColorMode"
              value="shift"
              checked={colorMode === 'shift'}
              onChange={() => onColorModeChange('shift')}
              className="w-4 h-4 text-blue-600"
            />
            <span>Shift</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              name="monthColorMode"
              value="position"
              checked={colorMode === 'position'}
              onChange={() => onColorModeChange('position')}
              className="w-4 h-4 text-blue-600"
            />
            <span>Position</span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b bg-white flex-shrink-0">
        {weekDayHeaders.map((day) => (
          <div
            key={day.toISOString()}
            className="px-2 py-2 text-center text-xs font-semibold text-gray-600 border-r last:border-r-0"
          >
            {format(day, 'EEE')}
          </div>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        <div
          className="grid grid-cols-7 auto-rows-fr min-h-full"
          style={{ gridTemplateRows: `repeat(${Math.ceil(gridDays.length / 7)}, minmax(110px, 1fr))` }}
        >
          {gridDays.map((day) => {
            const inMonth = isSameMonth(day, month);
            const dateStr = format(day, 'yyyy-MM-dd');
            const rows = inMonth ? rowsByDate.get(dateStr) ?? [] : [];
            const visible = rows.slice(0, MAX_VISIBLE_ROWS);
            const overflow = rows.length - visible.length;
            const isToday = isSameDay(day, new Date());
            const hasHoliday = inMonth && holidayDates.has(dateStr);

            return (
              <div
                key={dateStr}
                className={`border-r border-b p-1.5 flex flex-col min-h-[110px] ${
                  inMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'
                } ${isToday && inMonth ? 'ring-2 ring-inset ring-blue-400' : ''}`}
              >
                <div className="flex items-center justify-between mb-1 flex-shrink-0">
                  <span
                    className={`text-xs font-semibold ${
                      inMonth ? (isToday ? 'text-blue-700' : 'text-gray-800') : 'text-gray-400'
                    }`}
                  >
                    {format(day, 'd')}
                  </span>
                  {hasHoliday && (
                    <span className="text-[10px] px-1 rounded bg-amber-100 text-amber-800">Leave</span>
                  )}
                </div>

                {inMonth && (
                  <div className="flex-1 min-h-0 space-y-0.5 overflow-hidden">
                    {visible.map((row) => (
                      <div
                        key={row.shift.id}
                        className="text-[11px] leading-tight px-1 py-0.5 rounded overflow-hidden"
                        style={{
                          backgroundColor: `${row.color}22`,
                          borderLeft: `2px solid ${row.color}`,
                        }}
                        title={`${row.employeeName} ${row.startLabel}–${row.endLabel}${row.marketplaceBadge ? ` · ${row.marketplaceBadge}` : ''}`}
                      >
                        <span className="font-medium text-gray-900">{row.employeeName}</span>
                        <span className="text-gray-600"> {row.startLabel}–{row.endLabel}</span>
                        {row.marketplaceRequest && (
                          <MarketplaceCoverBadge request={row.marketplaceRequest} variant="onLight" />
                        )}
                      </div>
                    ))}
                    {overflow > 0 && (
                      <button
                        type="button"
                        onClick={() => setModalDate(day)}
                        className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline px-1 pt-0.5 text-left w-full"
                      >
                        +{overflow} more
                      </button>
                    )}
                    {rows.length === 0 && (
                      <div className="text-[11px] text-gray-300 px-1 pt-1">—</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {modalDate && (
        <SchedulingDayShiftsModal
          date={modalDate}
          rows={modalRows}
          onClose={() => setModalDate(null)}
        />
      )}
    </div>
  );
}
